import {
  facultyForcesProgramNa,
  isQueensStandardFaculty,
  QUEENS_FACULTY_NA,
  QUEENS_FACULTY_NONE,
  QUEENS_FACULTY_OTHER,
  QUEENS_PROGRAM_NA,
  queensFacultyPickerLabel,
  type QueensFacultyPickerId,
} from '../data/queensAcademicPickerConstants';
import { EXPECTED_GRADUATION_MONTH_OPTIONS } from '../data/expectedGraduationOptions';
import { programsForQueensFaculty, QUEENS_FACULTY_OPTIONS, queensFacultyLabel } from '../data/queensUniversityAcademics';
import type { QueensAcademicSelection } from '../components/tenant/QueensFacultyProgramPicker';

export interface StoredQueensAcademics {
  school: string | null;
  program: string | null;
  expectedGraduationTerm: string | null;
  expectedGraduationYear: string | null;
}

export function emptyQueensAcademics(): QueensAcademicSelection {
  return {
    facultyId: null,
    facultyCustom: '',
    programKind: null,
    programListValue: null,
    programOtherText: '',
    expectedGraduationMonth: null,
    expectedGraduationYear: null,
  };
}

export function academicsFromStored(stored: StoredQueensAcademics | null | undefined): QueensAcademicSelection {
  const school = stored?.school?.trim() ?? '';
  const program = stored?.program?.trim() ?? '';
  let facultyId: QueensFacultyPickerId | null = null;
  let facultyCustom = '';
  if (school === "No faculty at Queen's") facultyId = QUEENS_FACULTY_NONE;
  else if (school === 'N/A') facultyId = QUEENS_FACULTY_NA;
  else {
    const match = QUEENS_FACULTY_OPTIONS.find((faculty) => faculty.label === school);
    if (match) facultyId = match.id;
    else if (school) {
      facultyId = QUEENS_FACULTY_OTHER;
      facultyCustom = school;
    }
  }

  let programKind: QueensAcademicSelection['programKind'] = null;
  let programListValue: string | null = null;
  let programOtherText = '';
  if (facultyForcesProgramNa(facultyId) || program === 'N/A') {
    programKind = 'na';
  } else if (isQueensStandardFaculty(facultyId) && programsForQueensFaculty(facultyId).includes(program)) {
    programKind = 'list';
    programListValue = program;
  } else if (program) {
    programKind = 'other';
    programOtherText = program;
  }

  const month =
    EXPECTED_GRADUATION_MONTH_OPTIONS.find((option) => option.value === stored?.expectedGraduationTerm)?.value ?? null;

  return {
    facultyId,
    facultyCustom,
    programKind,
    programListValue,
    programOtherText,
    expectedGraduationMonth: month,
    expectedGraduationYear: stored?.expectedGraduationYear?.trim() || null,
  };
}

export function storedFromAcademics(selection: QueensAcademicSelection): {
  school: string;
  program: string;
  expectedGraduationTerm: string;
  expectedGraduationYear: string;
} {
  const school =
    queensFacultyPickerLabel(
      selection.facultyId,
      selection.facultyCustom,
      isQueensStandardFaculty(selection.facultyId) ? queensFacultyLabel(selection.facultyId) : null,
    ) ?? '';
  let program = '';
  if (facultyForcesProgramNa(selection.facultyId) || selection.programKind === 'na') program = QUEENS_PROGRAM_NA;
  else if (selection.programKind === 'other') program = selection.programOtherText.trim();
  else program = selection.programListValue ?? '';
  return {
    school,
    program,
    expectedGraduationTerm: selection.expectedGraduationMonth ?? '',
    expectedGraduationYear: selection.expectedGraduationYear ?? '',
  };
}

export function queensAcademicsComplete(selection: QueensAcademicSelection): boolean {
  if (!selection.facultyId) return false;
  if (selection.facultyId === QUEENS_FACULTY_OTHER && !selection.facultyCustom.trim()) return false;
  if (!(facultyForcesProgramNa(selection.facultyId) || selection.programKind === 'na')) {
    if (selection.programKind === 'other') {
      if (!selection.programOtherText.trim()) return false;
    } else if (!selection.programListValue?.trim()) return false;
  }
  return Boolean(selection.expectedGraduationMonth && selection.expectedGraduationYear);
}
